import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiUser, FiMail, FiPhone, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { COUNTRY_CODES } from '../constants/countryCodes';

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [countryCode, setCountryCode] = useState('+91');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const validate = () => {
        const e = {};
        if (!firstName.trim()) e.firstName = 'First name is required';
        if (!lastName.trim()) e.lastName = 'Last name is required';
        if (!/\S+@\S+\.\S+/.test(email)) e.email = 'Valid email required';
        const selectedCountry = COUNTRY_CODES.find(c => c.code === countryCode);
        const expectedDigits = selectedCountry ? selectedCountry.digits : 10;
        if (!/^\d+$/.test(phone) || phone.length < 7 || phone.length > expectedDigits + 2)
            e.phone = `Enter valid ${expectedDigits}-digit mobile number`;
        if (password.length < 6) e.password = 'Password must be at least 6 characters';
        if (password !== confirm) e.confirm = 'Passwords do not match';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setLoading(true);
        try {
            await register({ firstName, lastName, email, phone, password });
            navigate('/dashboard');
        } catch (err) {
            const msg = err.response?.data?.message || 'Registration failed. Please try again.';
            toast.error(msg);
            setErrors({ general: msg });
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = { paddingLeft: 42 };
    const iconStyle = {
        position: 'absolute', left: 14, top: '50%',
        transform: 'translateY(-50%)', color: '#64748b', fontSize: 16,
        pointerEvents: 'none'
    };

    return (
        <div
            className="cosmic-bg"
            style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '100px 20px 40px' }}
        >
            <div style={{ width: '100%', maxWidth: 520 }}>

                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    
                    <h1 className="text-gradient-purple" style={{ fontSize: 28, fontWeight: 800, fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: 8 }}>
                        Create Account
                    </h1>
                    <p style={{ color: '#64748b', fontSize: 14 }}>Begin your numerological journey today</p>
                </div>

                <div className="glass-card" style={{ padding: 36 }}>

                    {/* General error */}
                    {errors.general && (
                        <div style={{
                            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                            borderRadius: 10, padding: '12px 16px', marginBottom: 20, color: '#f87171', fontSize: 14
                        }}>
                            ⚠️ {errors.general}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} noValidate>

                        {/* First + Last Name */}
                        <div className="form-grid-2">
                            {/* First Name */}
                            <div className="form-group">
                                <label className="form-label">First Name</label>
                                <div style={{ position: 'relative' }}>
                                    <FiUser style={iconStyle} />
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="John"
                                        style={inputStyle}
                                        value={firstName}
                                        onChange={e => setFirstName(e.target.value)}
                                        autoComplete="given-name"
                                    />
                                </div>
                                {errors.firstName && <p className="form-error">{errors.firstName}</p>}
                            </div>

                            {/* Last Name */}
                            <div className="form-group">
                                <label className="form-label">Last Name</label>
                                <div style={{ position: 'relative' }}>
                                    <FiUser style={iconStyle} />
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Doe"
                                        style={inputStyle}
                                        value={lastName}
                                        onChange={e => setLastName(e.target.value)}
                                        autoComplete="family-name"
                                    />
                                </div>
                                {errors.lastName && <p className="form-error">{errors.lastName}</p>}
                            </div>
                        </div>

                        {/* Email */}
                        <div className="form-group">
                            <label className="form-label">Email Address</label>
                            <div style={{ position: 'relative' }}>
                                <FiMail style={iconStyle} />
                                <input
                                    type="email"
                                    className="form-control"
                                    placeholder="john@example.com"
                                    style={inputStyle}
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    autoComplete="email"
                                />
                            </div>
                            {errors.email && <p className="form-error">{errors.email}</p>}
                        </div>

                        {/* Phone */}
                        <div className="form-group">
                            <label className="form-label">Mobile Number</label>
                            <div style={{ display: 'flex', gap: 8 }}>
                                {/* Country Code Selector */}
                                <div style={{ position: 'relative', flexShrink: 0 }}>
                                    <select
                                        id="register-country-code"
                                        className="form-control"
                                        value={countryCode}
                                        onChange={e => { setCountryCode(e.target.value); setPhone(''); }}
                                        style={{
                                            width: 110,
                                            paddingLeft: 10,
                                            paddingRight: 28,
                                            fontWeight: 600,
                                            fontSize: 14,
                                            appearance: 'none',
                                            WebkitAppearance: 'none',
                                            cursor: 'pointer',
                                            background: 'rgba(255,255,255,0.04)',
                                            backgroundImage: 'none',
                                        }}
                                    >
                                        {COUNTRY_CODES.map((c, i) => (
                                            <option key={`${c.code}-${i}`} value={c.code}>
                                                {c.code} {c.name}
                                            </option>
                                        ))}
                                    </select>
                                    <span style={{
                                        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                                        pointerEvents: 'none', color: '#64748b', fontSize: 11, lineHeight: 1,
                                    }}>▼</span>
                                </div>
                                {/* Number Input */}
                                <div style={{ position: 'relative', flex: 1 }}>
                                    <FiPhone style={iconStyle} />
                                    <input
                                        id="register-mobile-number"
                                        type="tel"
                                        className="form-control"
                                        placeholder={`${(COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0]).digits}-digit number`}
                                        style={inputStyle}
                                        value={phone}
                                        onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, (COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0]).digits + 2))}
                                        autoComplete="tel"
                                    />
                                </div>
                            </div>
                            {errors.phone && <p className="form-error">{errors.phone}</p>}
                        </div>

                        {/* Password */}
                        <div className="form-group">
                            <label className="form-label">Password</label>
                            <div style={{ position: 'relative' }}>
                                <FiLock style={iconStyle} />
                                <input
                                    type={show ? 'text' : 'password'}
                                    className="form-control"
                                    placeholder="Min 6 characters"
                                    style={{ paddingLeft: 42, paddingRight: 44 }}
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShow(s => !s)}
                                    style={{
                                        position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                                        background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 16, padding: 0
                                    }}
                                    tabIndex={-1}
                                >
                                    {show ? <FiEyeOff /> : <FiEye />}
                                </button>
                            </div>
                            {errors.password && <p className="form-error">{errors.password}</p>}
                        </div>

                        {/* Confirm Password */}
                        <div className="form-group">
                            <label className="form-label">Confirm Password</label>
                            <div style={{ position: 'relative' }}>
                                <FiLock style={iconStyle} />
                                <input
                                    type="password"
                                    className="form-control"
                                    placeholder="Repeat your password"
                                    style={inputStyle}
                                    value={confirm}
                                    onChange={e => setConfirm(e.target.value)}
                                    autoComplete="new-password"
                                />
                            </div>
                            {errors.confirm && <p className="form-error">{errors.confirm}</p>}
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary"
                            style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
                            disabled={loading}
                        >
                            {loading ? '⏳ Creating Account...' : 'Create Account'}
                        </button>

                    </form>

                    <div className="divider" style={{ margin: '24px 0' }} />
                    <p style={{ textAlign: 'center', color: '#64748b', fontSize: 14 }}>
                        Already have an account?{' '}
                        <Link to="/login" style={{ color: '#7b96fa', fontWeight: 600, textDecoration: 'none' }}>
                            Sign in →
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
